<script lang="ts">
  import { defineComponent } from "vue"
  import { MeterReadingsService } from "@/services/operations/MeterReadingsService"
  import { MeterAppointmentService } from "@/services/MeterAppointmentService"
  import { AccountService } from "@/services/AccountService"
  import {
    appointmentsToPhotoItems,
    tagPhotoItemsForAccount,
  } from "@/models/meterPhotos/meterPhotos.parser"
  import { IMeterReadingPhotoItem } from "@/models/meterPhotos/meterPhotos.types"

  // Renderless component: fetches a single (previous) account's customer +
  // site-visit photos, tags them as previous-account rows, and streams them to
  // the parent. The parent mounts one of these per previous account, one at a
  // time, so the queries fire sequentially after the current account loads.
  export default defineComponent({
    name: "SiteAccountPhotoRows",
    props: {
      accountNumber: {
        type: String,
        required: true,
      },
      // 'Newer account' or 'Previous account' relative to the viewed account.
      relationLabel: {
        type: String,
        required: true,
      },
    },
    emits: ["update:rows", "settled"],
    data() {
      return {
        photoEvidenceQuery: MeterReadingsService.getPhotoEvidence(
          this.accountNumber,
        ),
        coreIdQuery: AccountService.getCoreIdByAccountNumber(
          this.accountNumber,
        ),
        hasSettled: false,
      }
    },
    computed: {
      appointmentsQuery() {
        if (!this.coreIdQuery.data) return null
        return MeterAppointmentService.getAppointments(this.coreIdQuery.data)
      },
      rows(): IMeterReadingPhotoItem[] {
        const customerPhotos = this.photoEvidenceQuery.data ?? []
        const siteVisitPhotos = this.appointmentsQuery?.data
          ? appointmentsToPhotoItems(this.appointmentsQuery.data)
          : []
        return tagPhotoItemsForAccount(
          [...customerPhotos, ...siteVisitPhotos],
          {
            accountNumber: this.accountNumber,
            otherAccountLabel: this.relationLabel,
          },
        )
      },
      isSettled(): boolean {
        // Done when both queries have completed once. Appointments only run when
        // a coreId resolved, so a null coreId means there are none to wait for.
        const photosDone = this.photoEvidenceQuery.isFetched
        const coreIdDone = this.coreIdQuery.isFetched
        const appointmentsDone =
          !this.coreIdQuery.data || Boolean(this.appointmentsQuery?.isFetched)
        return photosDone && coreIdDone && appointmentsDone
      },
    },
    watch: {
      rows: {
        immediate: true,
        handler(rows: IMeterReadingPhotoItem[]) {
          this.$emit("update:rows", rows)
        },
      },
      isSettled: {
        immediate: true,
        handler(settled: boolean) {
          if (settled && !this.hasSettled) {
            this.hasSettled = true
            this.$emit("settled")
          }
        },
      },
    },
    render() {
      return null
    },
  })
</script>
